import { Component, OnInit } from '@angular/core';
import { debounceTime, merge, startWith, Subject, takeUntil } from 'rxjs';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';
import { TableUtils } from 'src/app/utils/table-utils';
import { TableColumn } from '../../../shared-components/data-table/data-table.component';
import { DialogService } from 'src/app/services/dialog.service';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { ToastrService } from 'ngx-toastr';
import { IncoActualValidationMeasures, IncoActualValidationMeasuresResponse } from 'src/app/interfaces/investmentcontrolling/inco-actual-reporting-response';
import { IncoPeriodsInfo } from 'src/app/interfaces/investmentcontrolling/inco-periods-info';

@Component({
  selector: 'app-act-validation',
  templateUrl: './act-validation.component.html',
  styleUrls: ['./act-validation.component.less'],
  standalone: false
})
export class ActValidationComponent implements OnInit {
  isLoading: boolean = false;
  currencyShort: string = 'Local Currency';
  currencySymbol: string = '-';
  periods!: IncoPeriodsInfo;
  verificationStatus: 'success' | 'error' | '' = '';
  isVerified: boolean = false;
  showOnlyErrors: boolean = false;
  showColumnFilters: boolean = false;
  tableColumns: TableColumn[] = [];
  validationMeasures: IncoActualValidationMeasures[] = [];
  displayedMeasures: IncoActualValidationMeasures[] = [];

  private readonly destroy$ = new Subject<void>();
  rowValidationStatus: Map<string, 'valid' | 'invalid'> = new Map();
  cellValidationStatus: Map<string, 'valid' | 'invalid'> = new Map();

  toggleShowOnlyErrors(): void {
    this.showOnlyErrors = !this.showOnlyErrors;
    this.updateDisplayedMeasures();
  }

  private updateDisplayedMeasures(): void {
    this.displayedMeasures = this.showOnlyErrors
      ? this.validationMeasures.filter(row => this.rowValidationStatus.get(row.managementId) === 'invalid')
      : this.validationMeasures;
  }

  constructor(
    public defaults: DefaultValuesService,
    private incoService: InvestmentControllingService,
    private readonly toastrService: ToastrService,
    private readonly dialog: DialogService,
  ) { }

  ngOnInit(): void {
    this.setupSubscriptions();
  }

  private setupSubscriptions(): void {
    merge(
      this.defaults.fiscalYearMessageChanges$,
      this.defaults.quarter$
    ).pipe(
      startWith(null),
      debounceTime(50),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeColumns(): void {
    const currentLabel = this.periods?.current?.label || '-';
    const currentHeaderMain = TableUtils.getLabelWithoutYear(currentLabel);
    const currentHeaderSub = TableUtils.getYearPart(currentLabel);


    const baseColumns: TableColumn[] = [
      {
        key: 'managementBu',
        header: 'BU',
        width: '10%',
        align: 'left',
        editable: false,
        filterable: true,
      },
      {
        key: 'managementId',
        header: 'Management ID',
        width: '13%',
        align: 'left',
        editable: false,
        filterable: true,
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.rowValidationStatus.get(row.managementId);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'managementName',
        header: 'Management Name',
        width: '35%',
        align: 'left',
        editable: false,
        filterable: true,
      },
      {
        key: 'localCurrency',
        header: 'Local Currency',
        width: '10%',
        align: 'left',
        editable: false,
      },
      {
        key: 'currentTotal',
        header: currentHeaderMain || currentLabel,
        headerSub: currentHeaderSub,
        width: '16%',
        align: 'right',
        type: 'currency',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        },
        cellClass: (_value, row, _rowIndex) => {
          if (!row?.managementId) return null;
          const status = this.cellValidationStatus.get(`${row.managementId}_currentTotal`);
          return TableUtils.getValidationClass(status);
        }
      },
      {
        key: 'sapCurrentTotal',
        header: `${currentHeaderMain || currentLabel} SAP`,
        headerSub: currentHeaderSub,
        width: '16%',
        align: 'right',
        editable: false,
        formatter: (value, _row, index) => {
          return TableUtils.formatCurrency(value);
        }
      }
    ];

    this.tableColumns = [...baseColumns];
  }
  
  private quarterToPeriod(quarter: number): number {
    return quarter * 3;
  }

  loadData(showLoading: boolean = true, force: boolean = false): void {
    if (showLoading) {
      this.isLoading = true;
    }
    this.verificationStatus = '';
    
    const currentQuarter = this.defaults.quarterId;
    if (!currentQuarter) {
      this.toastrService.error('Quarter is not selected. Cannot view change log.');
      return;
    }

    const period = this.quarterToPeriod(currentQuarter);
    this.incoService.loadActValidation(
      this.defaults.fiscalYear,
      period,
      this,
      (tthis: ActValidationComponent, result: IncoActualValidationMeasuresResponse) => {
        tthis.periods = result.periods;
        tthis.validationMeasures = result.measures;
        tthis.initializeColumns();
        tthis.isLoading = false;
        tthis.showOnlyErrors = false;
        tthis.rowValidationStatus.clear();
        tthis.cellValidationStatus.clear();
        tthis.updateDisplayedMeasures();
      },
      (tthis: ActValidationComponent, error: any) => {
        tthis.dialog.showErrorDialog(error);
        tthis.isLoading = false;
      }
    );
  }

  onClickVerify(): void {
    this.rowValidationStatus.clear();
    this.cellValidationStatus.clear();

    let allRowsValid = true;

    for (let rowIndex = 0; rowIndex < this.validationMeasures.length; rowIndex++) {
      const row = this.validationMeasures[rowIndex];
      const id = row.managementId;

      const currentValid = row.currentTotal.toFixed(2) === row.sapCurrentTotal.toFixed(2);

      this.cellValidationStatus.set(`${id}_currentTotal`, currentValid ? 'valid' : 'invalid');

      const rowIsValid = currentValid;
      this.rowValidationStatus.set(id, rowIsValid ? 'valid' : 'invalid');
      if (!rowIsValid) allRowsValid = false;
    }

    if (allRowsValid) {
      this.verificationStatus = 'success';
      this.isVerified = true;
      this.toastrService.success('All rows match SAP values.');
    } else {
      this.verificationStatus = 'error';
      this.isVerified = false;
      this.toastrService.warning('Some rows do not match with SAP. Please check the highlighted rows in red.');
    }

    this.updateDisplayedMeasures();
    this.initializeColumns();
  }
}
